import { KeyboardEvent } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { colors } from 'styles/theme/colors';

type Props = {
  draft: string;
  onChange: (value: string) => void;
  onSend: () => void;
  // Terminal (completed/canceled) referral → read-only. The server 422 is the backstop.
  disabled: boolean;
  isSending: boolean;
};

// The plain message composer — a structural sibling of ActivityInput (bordered box +
// actions bar), but deliberately pared down: text + Send only. No Tag/Attach affordances
// (mentions are OLIO-2837; attachments are cut from v1). Enter sends, Shift+Enter newlines.
export default function Composer({ draft, onChange, onSend, disabled, isSending }: Props) {
  const canSend = draft.trim().length > 0 && !disabled && !isSending;

  const submit = () => {
    if (canSend) onSend();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <Container data-testid='chat-composer'>
      <TextArea
        data-testid='chat-composer-input'
        value={draft}
        disabled={disabled}
        placeholder={disabled ? 'This conversation is read-only.' : 'Start typing here to add a note.'}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      <ActionsBar>
        <SubmitButton data-testid='chat-send' disabled={!canSend} loading={isSending} onClick={submit}>
          Send
        </SubmitButton>
      </ActionsBar>
    </Container>
  );
}

const Container = styled.div`
  margin-top: 16px;
  background-color: ${colors.white};
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border: 1px solid ${colors.black10};
  display: flex;
  flex-direction: column;

  &:focus-within {
    border-color: ${colors.blue100};
  }
`;

const TextArea = styled.textarea`
  border: none;
  resize: none;
  width: 100%;
  box-sizing: border-box;
  padding: 16px;
  min-height: 72px;
  font-family: inherit;
  font-size: 14px;
  line-height: 24px;
  color: ${colors.black100};
  background: transparent;

  &::placeholder {
    color: ${colors.black50};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background: transparent;
    cursor: not-allowed;
  }
`;

const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  border-top: 1px solid ${colors.black10};
  padding: 8px 16px;
`;

const SubmitButton = styled(Button)`
  padding: 3px 18px;
  font-size: 10px;
  line-height: 14px;
  border: 2px solid transparent;
  margin-left: auto;
`;
